import { ApiError, type ApiErrorBody } from '../Models/ApiModel';
import { appConfig } from '../Utils/AppConfig';

/**
 * The only place the application talks to the network.
 *
 * Components never call fetch: they call a service, and every service goes
 * through here. That is what makes the token, the error shape and the 401
 * handling one decision each rather than thirty.
 */

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;
let onUnauthenticated: () => void = () => {};

/**
 * Installed once at startup by the auth layer. Keeping it a function rather
 * than a stored string means every request gets a fresh token, so an expired
 * one is refreshed by the identity provider instead of failing here.
 */
export function configureHttp(options: {
  getToken: TokenProvider;
  onUnauthenticated?: () => void;
}): void {
  tokenProvider = options.getToken;
  if (options.onUnauthenticated) onUnauthenticated = options.onUnauthenticated;
}

export interface RequestOptions {
  /** Public token routes are called without a session; nothing to attach. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!options.anonymous) {
    const token = await tokenProvider();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (options.signal) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(`${appConfig.apiBase}${path}`, init);
  } catch {
    // A failed fetch is the network, not the API, and has no code of its own.
    throw new ApiError(0, 'NETWORK', 'Could not reach the server');
  }

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = (payload ?? {}) as Partial<ApiErrorBody>;
    if (response.status === 401) onUnauthenticated();
    throw new ApiError(
      response.status,
      error.code ?? 'SERVER_ERROR',
      error.message ?? 'Request failed'
    );
  }

  return payload as T;
}

/**
 * A file rather than JSON. The API reads the token from a header and nowhere
 * else, so a plain link to a protected download answers 401: the browser has
 * no way to attach one. The headers come back too, because a download can
 * carry facts about the file it holds.
 */
async function download(path: string): Promise<{ blob: Blob; headers: Headers }> {
  const headers: Record<string, string> = {};
  const token = await tokenProvider();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${appConfig.apiBase}${path}`, { method: 'GET', headers });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Could not reach the server');
  }

  if (!response.ok) {
    const text = await response.text();
    let error: Partial<ApiErrorBody> = {};
    try { error = text ? JSON.parse(text) as Partial<ApiErrorBody> : {}; } catch { /* not JSON */ }
    if (response.status === 401) onUnauthenticated();
    throw new ApiError(response.status, error.code ?? 'SERVER_ERROR', error.message ?? 'Request failed');
  }

  return { blob: await response.blob(), headers: response.headers };
}

export const http = {
  download,
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body ?? {}, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PUT', path, body ?? {}, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body ?? {}, options),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('DELETE', path, body, options)
};
